import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardProps, Box, Portal, Grid2, Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { ReactComponent as NutritionTargetsIcon } from "../../icons/NutritionTargets.svg";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { DeleteConfirmationProvider } from "../program/modals/DeleteConfirmation.tsx/DeleteConfirmationModalContext";

import NutritionEntriesService from "../../services/NutritionEntriesService";
import dayjs from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
import NutritionTargetsService from "../../services/NutritionTargetsService";
import { NewEntriesDrawer } from "./entries/NewEntriesDrawer";
import { NewDrawer } from "./targets/NewDrawer";
import { ClientNutritionDayChart } from "./ClientNutritionDayChart";
import { ClientNutritionCardTable } from "./ClientNutritionCardTable";
import ClientNutritionCardBarChart from "./ClientNutritionCardBarChart";
import { chunkBy, chunkKeepTail, interpolateNutritionData } from "../../utils/chart";
import { TimeAgo } from "../../routes/utils/TimeAgo";
import { ClientNutritionCardTableNoTarget } from "./ClientNutritionCardTableNoTarget";
import { useActivityContext } from "../../contexts/ActivityContext";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { JourneyAnchors } from "../../constants";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import DisabledCheckInTooltip from "../new-editor/components/tooltips/DisabledCheckInTooltip";
import { ButtonNav } from "./ButtonNav";
import SettingsPopup from "../charts/SettingsPopup";
import SettingsPopupOption from "../charts/SettingsPopupOption";
import { Calendar, ChartColumn, ChartColumnBig } from "lucide-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";

dayjs.extend(dayOfYear);

export const VERTICAL_TRANSITION_WIDTH = 1050;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    width: "100%",

    [theme.breakpoints.down(VERTICAL_TRANSITION_WIDTH)]: {
      padding: theme.spacing(2),
    },

    [theme.containerQueries.down(VERTICAL_TRANSITION_WIDTH)]: {
      padding: theme.spacing(2),
    },
  },

  title: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: "29px",
    color: theme.palette.common.black,
    marginBottom: 4,
  },

  subtitle: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "20px",
    color: theme.palette.text.secondary,
  },

  mainContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",

    [theme.breakpoints.down(VERTICAL_TRANSITION_WIDTH)]: {
      flexDirection: "column",
    },

    [theme.containerQueries.down(VERTICAL_TRANSITION_WIDTH)]: {
      flexDirection: "column",
    },
  },

  navItemsContainer: {
    position: "absolute",
    left: "35%",
  },

  mdUp: {
    display: "block",

    [theme.breakpoints.down("md")]: {
      display: "none",
    },

    [theme.containerQueries.down("md")]: {
      display: "none",
    },
  },

  mdDown: {
    display: "none",

    [theme.breakpoints.down("md")]: {
      display: "block",
    },

    [theme.containerQueries.down("md")]: {
      display: "block",
    },
  },
}));

export enum TimeUnit {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
  ALLTIME = "alltime",
}

export interface ClientNutritionCardProps extends CardProps {
  client?: { clientId?: number; username?: string };
  refresh?: () => void;
  handleAddNutrition?: any;
  isProgram?: boolean;
  readonly?: boolean;
}

interface Settings {
  pointsSum: boolean;
  alignToRange: boolean;
}

export const CLIENT_NUTRITION_ENTRIES_QUERY_KEY =
  "client-nutrition-entries-key";
export const CLIENT_NUTRITION_TARGETS_QUERY_KEY =
  "client-nutrition-targets-key";
export const CLIENT_NUTRITION_HIDDEN_TARGETS_QUERY_KEY =
  "client-nutrition-hidden-targets-key";
export const CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY =
  "client-nutrition-target-recomendations-key";

export function ClientNutritionCard(props: ClientNutritionCardProps) {
  const {
    className,
    client,
    refresh,
    handleAddNutrition,
    isProgram,
    readonly,
    ...other
  } = props;
  const s = useStyles();

  const { username } = client || {};

  const date = useActivityContext()?.date as dayjs.Dayjs;

  const [targetsDrawerOpen, setTargetsDrawerOpen] = useState(false);
  const [entriesDrawerOpen, setEntriesDrawerOpen] = useState(false);
  const [entriesDrawerRendered, setEntriesDrawerRendered] = useState(false);
  const [settingsDialogAnchorEl, setSettingsDialogAnchorEl] =
      useState<HTMLElement | null>(null);
  const [settings, setSettings] = useLocalStorage<Settings>(
    "nutritionChartSettings",
    {
    pointsSum: false,
    alignToRange: true,
  })

  const handleSettingsChange = (field) => {
      return (value) =>
        setSettings((settings) => ({ ...settings, [field]: value }));
    };

  const { data: nutritionTargets, isLoading: isLoadingTargets } = useQuery({
    queryKey: [CLIENT_NUTRITION_TARGETS_QUERY_KEY, { username }],
    queryFn: () => NutritionTargetsService.getNutritionTargets(username),
    enabled: !!username,
    placeholderData: keepPreviousData,
  });

  const { data: nutritionEntries, isLoading: isLoadingEntries } = useQuery({
    queryKey: [CLIENT_NUTRITION_ENTRIES_QUERY_KEY, { username }],
    queryFn: () => NutritionEntriesService.getNutritionEntries(username),
    enabled: !!username,
    placeholderData: keepPreviousData,
  });

  const sortedNutritionEntries = useMemo(
    () => nutritionEntries?.toSorted((a, b) => a.date.diff(b.date)),
    [nutritionEntries],
  );

  const lastEntry = useMemo(
    () =>
      sortedNutritionEntries?.length &&
      sortedNutritionEntries[sortedNutritionEntries.length - 1],
    [sortedNutritionEntries],
  );

  const entriesStartDate = useMemo(
    () =>
      sortedNutritionEntries?.[0]?.date?.startOf("day") ||
      dayjs().startOf("day"),
    [sortedNutritionEntries],
  );
  const entriesEndDate = useMemo(
    () =>
      (lastEntry?.date?.isAfter?.(dayjs(), "day")
        ? lastEntry.date
        : dayjs()
      ).endOf("month"),
    [lastEntry],
  );

  const { data: targetRecomendations, isLoading: isLoadingRecomendations } =
    useQuery({
      queryKey: [
        CLIENT_NUTRITION_TARGET_RECOMENDATIONS_QUERY_KEY,
        { username, entriesStartDate, entriesEndDate },
      ],
      queryFn: () =>
        NutritionTargetsService.getRecomendedTargetList(
          username,
          entriesStartDate.year(),
          entriesStartDate.month() + 1,
          entriesStartDate.date(),
          entriesEndDate.year(),
          entriesEndDate.month() + 1,
          entriesEndDate.date(),
        ),
      enabled: !isLoadingEntries && !!username,
      placeholderData: keepPreviousData,
    });

  const [activeButton, setActiveButton] = useState<TimeUnit>(TimeUnit.DAY);
  const [navIndex, setNavIndex] = useState(0);

  const currentUser = useCurrentUser();
  const isCoachView = currentUser.role === "COACH";

  const alltimeAveragingPeriod = useMemo(() => {
    if (!nutritionEntries || !nutritionEntries.length) return "day";
    const firstEntry = nutritionEntries[0];
    const lastEntry = nutritionEntries[nutritionEntries.length - 1];
    const startDate = firstEntry?.date;
    const endDate = lastEntry?.date;
    const duration = startDate.diff(endDate, "weeks");
    if (duration <= 4) {
      return "day";
    } else if (duration <= 16) {
      return "week";
    } else if (duration <= 208) {
      return "month";
    } else {
      return "year";
    }
  }, [nutritionEntries]);

  const chartData = useMemo(() => {
    if (!sortedNutritionEntries || !nutritionTargets || !targetRecomendations)
      return [];

    const timeUnits = ["day", "week", "month", "year", undefined, "alltime"];
    const averagingWindow: any = timeUnits[timeUnits.indexOf(activeButton) - 1];

    const chunkParams = {
      day: ["dayOfYear", "year"],
      week: ["isoWeek", "year"],
      month: ["month", "year"],
    }

    const unaveragedChunkSizes = {
      day: 1,
      week: 7,
      month: 30,
      alltime: Infinity,
    };
    const averagedChunkSizes = {
      day: 1,
      week: 7,
      month: 4,
      alltime: Infinity,
    };

    const averagingPeriod = activeButton === "alltime" ?  alltimeAveragingPeriod : settings.pointsSum ? averagingWindow : "day"

    const interpolatedData = interpolateNutritionData(
        sortedNutritionEntries,
        nutritionTargets,
        targetRecomendations,
        activeButton === "alltime",
        averagingPeriod,
        false,
        false,
        date,
      )

    if (activeButton === "alltime") {
      return [
        interpolatedData
      ]
    }
    if (settings.alignToRange || activeButton === "day") {
      return chunkBy(
        interpolatedData.filter((entry) => date || !dayjs().isBefore(entry.date, activeButton)),
        chunkParams[activeButton],
        "date",
        "calories",
        false,
      );
    } else {
      return chunkKeepTail(
        interpolatedData.filter((entry) => date || !dayjs().isBefore(entry.date, activeButton)),
        settings.pointsSum ? averagedChunkSizes[activeButton] : unaveragedChunkSizes[activeButton],
        true,
        "protein"
      )
    }
  }, [sortedNutritionEntries, settings, nutritionTargets, targetRecomendations, date, activeButton]);
  
  const tableData = useMemo(() => {
    if (!nutritionEntries || !nutritionTargets || !targetRecomendations)
      return [];
    let chunkedData = [];
    switch (activeButton) {
      case "day":
        chunkedData = interpolateNutritionData(
          sortedNutritionEntries,
          nutritionTargets,
          targetRecomendations,
          false,
          "day",
          false,
          false,
          date,
        );
        break;
      case "week":
        chunkedData = interpolateNutritionData(
          nutritionEntries.toSorted((a, b) => a.date.diff(b.date)),
          nutritionTargets,
          targetRecomendations,
          false,
          "week",
          false,
          true,
        );
        break;
      case "month":
        chunkedData = interpolateNutritionData(
          nutritionEntries.toSorted((a, b) => a.date.diff(b.date)),
          nutritionTargets,
          targetRecomendations,
          false,
          "month",
          false,
          true,
        );
        break;
      case "alltime":
        chunkedData = chunkedData = interpolateNutritionData(
          nutritionEntries.toSorted((a, b) => a.date.diff(b.date)),
          nutritionTargets,
          targetRecomendations,
          false,
          "year",
          true,
          true,
        );
        break;
      default:
        chunkedData = [[]];
    }

    chunkedData = chunkedData.filter(
      ({ calories, protein, fat, carbs }) =>
        !(
          calories === undefined &&
          protein === undefined &&
          fat === undefined &&
          carbs === undefined
        ),
    );

    return chunkedData;
  }, [activeButton, nutritionEntries, nutritionTargets, targetRecomendations]);

  useEffect(() => {
    setNavIndex(chartData.length - 1);
  }, [chartData.length]);

  const handleToggleEntriesDrawer = () => {
    if (entriesDrawerOpen) {
      setEntriesDrawerOpen(false);
    } else {
      if (entriesDrawerRendered) {
        setEntriesDrawerOpen(true);
      } else {
        setEntriesDrawerRendered(true);
        setTimeout(() => {
          setEntriesDrawerOpen(true);
        }, 0);
      }
    }
  };

  const handleEditClick = useCallback(() => {
    !readonly &&
      (isCoachView
        ? setTargetsDrawerOpen((targetsDrawerOpen) => !targetsDrawerOpen)
        : handleToggleEntriesDrawer());
  }, [readonly, isCoachView]);

  const hasData = chartData.length > 0

  return (
    <DeleteConfirmationProvider>
      <DisabledCheckInTooltip visible={readonly}>
        <Card
          sx={{ ...(isProgram && { boxShadow: 0 }) }}
          className={clsx(s.root, className, JourneyAnchors.CLIENT_NUTRITIONS)}
          {...other}
        >
          <ClientDetailsCardHeader
            icon={<NutritionTargetsIcon />}
            title="Nutrition"
            onEditClick={handleEditClick}
            disableCompactEdit={Boolean(date) || readonly}
            disableOnEdit={
              isCoachView
                ? readonly || isLoadingTargets
                : isLoadingEntries ||
                  isLoadingTargets ||
                  isLoadingRecomendations
            }
            onSettingsClick={(e) => {
              setSettingsDialogAnchorEl(e.currentTarget as HTMLElement);
            }}
            disableSettings={
              activeButton === "day" || activeButton === "alltime"
            }
            subtitle={
              <>
                {nutritionEntries?.length > 0
                  ? nutritionEntries?.[0]?.isLastModifiedAutomatically
                    ? "Last synced "
                    : "Last updated "
                  : "Not updated yet"}
                <TimeAgo
                  date={nutritionEntries?.[0]?.lastModified?.toString()}
                />
              </>
            }
          >
            {hasData && !date && (
              <Box className={clsx(s.mdUp, s.navItemsContainer)}>
                <ButtonNav
                  {...{
                    activeButton,
                    setActiveButton,
                    navIndex,
                    setNavIndex,
                    dataLength: chartData?.length,
                  }}
                />
              </Box>
            )}
          </ClientDetailsCardHeader>
          {hasData ? (
            <>
              {!date && (
                <Box className={s.mdDown}>
                  <ButtonNav
                    {...{
                      activeButton,
                      setActiveButton,
                      navIndex,
                      setNavIndex,
                      dataLength: chartData?.length,
                    }}
                  />
                </Box>
              )}

              {/* END OF NUTRITION CARD HEADER */}

              <Box
                sx={{
                  mt: 4,
                }}
              >
                <Grid2 container spacing={1} className={s.mainContainer}>
                  <Grid2 sx={{ width: "100%", justifyItems: "center" }}>
                    {activeButton === "day" ? (
                      <ClientNutritionDayChart
                        data={chartData[navIndex]?.[0]}
                        targets={nutritionTargets}
                        client={client}
                        readonly={readonly}
                      />
                    ) : (
                      <ClientNutritionCardBarChart
                        data={chartData[navIndex]}
                        showWeekday={activeButton === "week"}
                        averagingWindow={
                          activeButton === "alltime" && alltimeAveragingPeriod
                        }
                      />
                    )}
                  </Grid2>
                  <Divider
                    orientation={"vertical"}
                    flexItem
                    sx={{ my: 1 }}
                    className={s.mdUp}
                  />
                  <Grid2 sx={{ width: "100%" }}>
                    {tableData[navIndex]?.targetId ? (
                      <ClientNutritionCardTable
                        data={tableData[navIndex]}
                        showAverageIntake={activeButton !== "day"}
                        disableTags={activeButton !== "day"}
                      />
                    ) : (
                      <ClientNutritionCardTableNoTarget
                        data={tableData[navIndex]}
                        showAverageIntake={activeButton !== "day"}
                        disableTags={activeButton !== "day"}
                      />
                    )}
                  </Grid2>
                </Grid2>
              </Box>
            </>
          ) : (
            // chartData.length <= 0
            <Box
              sx={{
                mt: 4,
              }}
            >
              <Grid2
                container
                spacing={1}
                sx={{
                  display: "flex",
                  flexDirection: {
                    "@xs": "column",
                    "@md": "row",
                    xs: "column",
                    md: "row",
                  },
                  flexWrap: "nowrap",
                  alignItems: "center",
                }}
              >
                <Grid2 sx={{ width: "100%", justifyItems: "center" }}>
                  <ClientNutritionDayChart
                    readonly={readonly}
                    client={client}
                  />
                </Grid2>
                <Divider
                  orientation={"vertical"}
                  flexItem
                  sx={{ my: 1 }}
                  className={s.mdUp}
                />
                <Grid2 sx={{ width: "100%" }}>
                  <ClientNutritionCardTable />
                </Grid2>
              </Grid2>
            </Box>
          )}
        </Card>
      </DisabledCheckInTooltip>

      {!readonly && isCoachView && !isLoadingTargets && (
        <Portal>
          <NewDrawer
            open={targetsDrawerOpen}
            onClose={() => setTargetsDrawerOpen(false)}
            nutritionTargets={nutritionTargets || []}
            clientInfo={client}
          />
        </Portal>
      )}

      {!readonly &&
        !isCoachView &&
        !isLoadingEntries &&
        !isLoadingTargets &&
        !isLoadingRecomendations &&
        entriesDrawerRendered && (
          <Portal>
            <NewEntriesDrawer
              open={entriesDrawerOpen}
              onClose={() => setEntriesDrawerOpen(false)}
              entries={nutritionEntries || []}
              nutritionTargets={nutritionTargets || []}
              targetRecomendations={targetRecomendations || []}
              clientInfo={client}
              isCoachView={isCoachView}
              date={date}
            />
          </Portal>
        )}

        <SettingsPopup
                anchorEl={settingsDialogAnchorEl}
                setAnchorEl={setSettingsDialogAnchorEl}
              >
                <SettingsPopupOption
                  title="Bars"
                  setting={settings.pointsSum}
                  handleSettingChange={handleSettingsChange("pointsSum")}
                  disabled={activeButton === "alltime"}
                  options={[
                    {
                      label: "All records",
                      description:
                        "See daily records within your selected time frame (week, month, year, or all-time).",
                      icon: <ChartColumn size={15} />,
                    },
                    {
                      label: "Sums",
                      description:
                        "See sum client progress, shown daily (for weeks), weekly (for months), monthly (for years)",
                      icon: <ChartColumnBig size={15} />,
                    },
                  ]}
                />
                <SettingsPopupOption
                  title="Alignment"
                  setting={settings.alignToRange}
                  handleSettingChange={handleSettingsChange("alignToRange")}
                  disabled={activeButton === "alltime"}
                  options={[
                    {
                      label: "Recent days",
                      description:
                        "Focus on the latest 7, 30, or 365 days of the data.",
                      icon: <ChartColumn size={15} />,
                    },
                    {
                      label: "Calendar view",
                      description:
                        "View data for ongoing calendar week, month, or year.",
                      icon: <Calendar size={15} />,
                    },
                  ]}
                />
              </SettingsPopup>
    </DeleteConfirmationProvider>
  );
}
